﻿    export type DirtyTracker<T extends object> = {
        /** L’objet PROXY sur lequel ton UI/formulaire travaille */
        proxy: T;
        /** a-t-on modifié depuis le dernier reset ? */
        isDirty: () => boolean;
        /** remet le flag à false (après sauvegarde par ex.) */
        reset: () => void;
        /** Renvoie le modèle du tracker au format json */
        toJson : ()  => T;
    };

    /** Optionnel : ignorer certains chemins (ex: champs UI transitoires) */
    type ShouldIgnore = (path: string[], prop: PropertyKey) => boolean;

    export class MakeDirty {
        
        #dirty = false;
        readonly #seen = new WeakMap<object, any>();
        readonly #shouldIgnore: ShouldIgnore

        constructor(options?: { shouldIgnore: ShouldIgnore }) {
            if (options != null)
                this.#shouldIgnore = options.shouldIgnore;
            
            return this;
        }
        
        public Track = <T extends object>(obj: T): DirtyTracker<T> => {
            const proxy = this.#wrap(obj, []);
            return {
                proxy,
                isDirty: () => this.#dirty,
                reset: () => {
                    this.#dirty = false;
                    console.log("Dirty Track : ", "Reset")
                },
                toJson : (): T => JSON.parse(JSON.stringify(proxy))
            }
        }
        
        #wrap = <T extends object>(target: T, path: string[]) => {
            if (this.#seen.has(target)) {
                return this.#seen.get(target);
            }

            const proxy = new Proxy(target, {
                get: (t, p, r) => {
                    const v = Reflect.get(t, p, r);
                    if (v && typeof v === 'object') {
                        if (v instanceof Date) return v;
                        if (v instanceof Map) return this.#wrapMap(v as Map<any, any>, path.concat(String(p)));
                        if (v instanceof Set) return this.#mapSet(v as Set<any>, path.concat(String(p)));
                        return this.#wrap(v as object, path.concat(p.toString()))
                    }

                    return v;
                },
                set: (t, p, value, r) => {
                    if (this.#shouldIgnore && this.#shouldIgnore(path, p))
                        return Reflect.set(t, p, value, r);

                    const old = (t as any)[p];
                    const changed = old !== value;
                    const ok = Reflect.set(t, p, value, r);

                    if (changed)
                        this.#dirty = true;

                    return ok;
                }, deleteProperty: (t, p) => {
                    if (this.#shouldIgnore && this.#shouldIgnore(path, p))
                        return Reflect.deleteProperty(t, p);

                    const existed = Object.prototype.hasOwnProperty.call(t, p);
                    const ok = Reflect.deleteProperty(t, p);
                    if (existed && ok)
                        this.#dirty = true;
                    return ok;
                },
                defineProperty: (t, p, desc) => {
                    if (this.#shouldIgnore && this.#shouldIgnore(path, p))
                        return Reflect.defineProperty(t, p, desc);

                    const ok = Reflect.defineProperty(t, p, desc);
                    this.#dirty = true;
                    return ok;
                }
            });

            this.#seen.set(target, proxy);
            return proxy;
        }

        #mapSet = <V>(s: Set<V>, path: string[]) => {
            if (this.#seen.has(s)) return this.#seen.get(s);
            const p = new Proxy(s, {
                get: (t, p, r) => {
                    if (p == "add")
                        return (value: V) => {
                            const sizeBefore = t.size;
                            const res = Set.prototype.add.call(t, value);
                            if (t.size !==sizeBefore)
                                this.#dirty = true;
                            return res;
                        }
                    
                    if (p == "delete") 
                        return (value:V) => {
                        const existed = t.delete(value);
                        if (existed)
                            this.#dirty = true;
                        
                        return existed;
                        }
                    
                    if (p == "clear")
                        return () => {
                            if (t.size > 0) this.#dirty = true;
                            return Set.prototype.clear.call(t);
                        }
                    
                    return Reflect.get(t, p, r);
                }
            })
            
            this.#seen.set(s, p);
            return p;
        }
        
        #wrapMap = <K,V>(m:Map<K,V>, path: string[]) => {
            if (this.#seen.has(m)) return this.#seen.get(m);
            
            const p = new Proxy(m, {
                get : (target, prop, receiver) => {
                    if (prop == "set")
                        return (key: K, value: V) => {
                        const existed = target.has(key);
                        const old = target.get(key);
                        const res = Map.prototype.set.call(target, key, value);
                        if (!existed || old !== value)
                            this.#dirty = true;
                        return res;
                        }
                    if (prop === 'delete') {
                        return (key: K) => {
                            const existed = target.delete(key);
                            if (existed) this.#dirty = true;
                            return existed;
                        };
                    }
                    if (prop === 'clear') {
                        return () => {
                            if (target.size > 0) this.#dirty = true;
                            return Map.prototype.clear.call(target);
                        };
                    }
                    return Reflect.get(target, prop, receiver);
                }
            });
            
            return p;
        }

    }